import { AsyncComponentLoader, Component, computed, defineAsyncComponent, markRaw } from 'vue';
import { useRoute } from 'vue-router';
import { useHLayouts } from './use-layouts';
import { useOSettings } from './use-settings';

const asyncLayoutCache = new Map<string, Component>();

function resolveAsyncLayout(loader: AsyncComponentLoader, cacheKey: string) {
  if (!asyncLayoutCache.has(cacheKey)) {
    asyncLayoutCache.set(cacheKey, markRaw(defineAsyncComponent(loader)));
  }

  return asyncLayoutCache.get(cacheKey)!;
}

/**
 * A Vue composable that provides layout management based on the current route.
 *
 * @function
 * @name useHRouteLayout
 * @description
 * This composable determines the layout component to be used for the current route.
 * It checks the route's metadata for a layout definition and uses it if available;
 * otherwise, it falls back to a default layout.
 *
 * @param {Component} fallbackLayout - The default layout component to use if no layout is specified in the route metadata.
 * @returns {Object} An object containing the following properties:
 * - `hLayout`: A computed property that returns the layout component to be used for the current route.
 */
export function useHRouteLayout(fallbackLayout: Component) {
  const route = useRoute();
  const { default_layout } = useOSettings();
  const layouts = useHLayouts();
  const rawFallbackLayout = markRaw(fallbackLayout);

  const hLayout = computed(() => {
    if (route?.meta?.layout) {
      const routeLayout = route.meta.layout as AsyncComponentLoader;
      return resolveAsyncLayout(routeLayout, `route:${String(route.name ?? route.path)}`);
    }

    const layoutName = default_layout?.value;
    const loader = layoutName ? layouts[layoutName] : undefined;

    if (loader) {
      return resolveAsyncLayout(loader, layoutName!);
    }

    return rawFallbackLayout;
  });

  return { hLayout };
}
